import React, { useState, useEffect } from "react";
import { Form, Row, Col, Card, Button, ListGroup, Badge, Spinner } from "react-bootstrap";
import AttachmentApi from "../../api/AttachmentApi";
import { FaTrash, FaDownload, FaFile } from "react-icons/fa";

export default function WorkflowAttachmentSelector({
  config,
  onChange,
  readOnly,
  eventType = "onUpdate", // Default to onUpdate if not provided
  workflowId,
  actionIndex,
}) {
  // If onCreate, force file source to be staticFile
  const defaultFileSource = eventType === "onCreate" ? "staticFile" : (config?.fileSource || "workflowObject");

  const [fileSource, setFileSource] = useState(defaultFileSource);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);

  // Automatically switch to staticFile when eventType changes to onCreate
  useEffect(() => {
    if (eventType === "onCreate" && fileSource !== "staticFile") {
      setFileSource("staticFile");
      onChange("fileSource", "staticFile");
    }
  }, [eventType]);

  // Load attachments when workflowId and actionIndex are available
  useEffect(() => {
    if (workflowId && typeof actionIndex === 'number' && fileSource === "staticFile") {
      loadAttachments();
    }
  }, [workflowId, actionIndex, fileSource]);

  // Helper function to extract value from field (handles both primitive and object formats)
  const getFieldValue = (field) => {
    if (field === null || field === undefined) return '';
    if (typeof field === 'object') {
      return field.value || field.label || '';
    }
    return field;
  };

  const loadAttachments = async () => {
    if (!workflowId || typeof actionIndex !== 'number') return;

    setLoading(true);
    try {
      const response = await AttachmentApi.getWorkflowAttachments(workflowId, actionIndex);
      if (response.success && response.data) {
        setUploadedFiles(response.data);
        // Store attachment IDs in config
        onChange("attachmentIds", response.data.map(f => f.id));
      }
    } catch (error) {
      console.error("Error loading attachments:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceChange = (e) => {
    const source = e.target.value;
    setFileSource(source);
    onChange("fileSource", source);
  };

  const handleStaticFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (!workflowId) {
      // If workflow not saved yet, store files temporarily
      onChange("staticFiles", files);
      return;
    }

    // Upload files immediately if workflow exists
    setUploading(true);
    try {
      const response = await AttachmentApi.uploadWorkflowAttachments(
        workflowId,
        actionIndex,
        files,
        config?.description || ''
      );

      if (response.success && response.data) {
        const newFiles = [...uploadedFiles, ...response.data];
        setUploadedFiles(newFiles);
        onChange("attachmentIds", newFiles.map(f => f.id));
        // Clear file input
        e.target.value = '';
      }
    } catch (error) {
      console.error("Error uploading files:", error);
      alert("Failed to upload files. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Are you sure you want to delete this file?")) {
      return;
    }

    try {
      const response = await AttachmentApi.deleteAttachment(fileId);
      if (response.success) {
        const updatedFiles = uploadedFiles.filter(f => f.id !== fileId);
        setUploadedFiles(updatedFiles);
        onChange("attachmentIds", updatedFiles.map(f => f.id));
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      alert("Failed to delete file. Please try again.");
    }
  };

  const handleDownloadFile = async (fileId, fileName) => {
    try {
      const blob = await AttachmentApi.downloadAttachment(fileId);
      if (!blob || blob.size === 0) {
        alert('Download failed: received empty file.');
        return;
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    }
  };

  const handleFieldChange = (field, value) => {
    onChange(field, value);
  };

  const fileMatchingTypes = [
    "Equals",
    "Not Equals",
    "Contains",
    "Not Contains",
    "Starts With",
    "Ends With",
  ];

  const fileTypes = ["Document", "Image", "Video"];

  return (
    <Card className="mt-3">
      <Card.Header>Attachment Rule</Card.Header>
      <Card.Body>
        <Form.Group>
          <Form.Label>File Source</Form.Label>
          <Form.Select
            value={fileSource}
            onChange={handleSourceChange}
            disabled={readOnly || eventType === "onCreate"}
          >
            <option value="workflowObject">From Workflow Object</option>
            <option value="staticFile">Static File</option>
          </Form.Select>
          {eventType === "onCreate" && (
            <small className="text-muted">
              Only static files are available for onCreate events (no files exist on new records)
            </small>
          )}
        </Form.Group>

        {fileSource === "staticFile" && (
          <>
            <Form.Group className="mt-2">
              <Form.Label>Select File(s)</Form.Label>
              <Form.Control
                type="file"
                multiple
                onChange={handleStaticFileChange}
                disabled={readOnly || uploading}
              />
              {uploading && (
                <div className="mt-2">
                  <Spinner size="sm" className="me-2" />
                  <span className="text-muted">Uploading files...</span>
                </div>
              )}
              {!workflowId && (
                <Form.Text className="text-muted">
                  Files will be uploaded when you save the workflow.
                </Form.Text>
              )}
            </Form.Group>

            {uploadedFiles.length > 0 && (
              <Form.Group className="mt-3">
                <Form.Label>Uploaded Files</Form.Label>
                {loading ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" />
                    <span className="ms-2 text-muted">Loading files...</span>
                  </div>
                ) : (
                  <ListGroup>
                    {uploadedFiles.map((file) => (
                      <ListGroup.Item
                        key={file.id}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <div className="d-flex align-items-center">
                          <FaFile className="me-2 text-muted" />
                          <div>
                            <div>{getFieldValue(file.file_name)}</div>
                            {file.file_size && (() => {
                              const size = getFieldValue(file.file_size);
                              return size ? (
                                <small className="text-muted">
                                  {(Number(size) / 1024).toFixed(2)} KB
                                </small>
                              ) : null;
                            })()}
                          </div>
                        </div>
                        <div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => handleDownloadFile(file.id, getFieldValue(file.file_name))}
                            disabled={readOnly}
                          >
                            <FaDownload />
                          </Button>
                          {!readOnly && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDeleteFile(file.id)}
                            >
                              <FaTrash />
                            </Button>
                          )}
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Form.Group>
            )}
          </>
        )}

        {fileSource === "workflowObject" && (
          <Row className="mt-2">
            <Col md={4}>
              <Form.Group>
                <Form.Label>File Matching Type</Form.Label>
                <Form.Select
                  value={config?.fileMatchingType || ""}
                  onChange={(e) =>
                    handleFieldChange("fileMatchingType", e.target.value)
                  }
                  required={!readOnly}
                  disabled={readOnly}
                >
                  <option value="">-Select Matching Type-</option>
                  {fileMatchingTypes?.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>File Type</Form.Label>
                <Form.Select
                  value={config?.fileType || ""}
                  onChange={(e) =>
                    handleFieldChange("fileType", e.target.value)
                  }
                  required={!readOnly}
                  disabled={readOnly}
                >
                  <option value="">-Select File Type-</option>
                  {fileTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>File Name</Form.Label>
                <Form.Control
                  type="text"
                  value={config?.fileName || ""}
                  onChange={(e) =>
                    handleFieldChange("fileName", e.target.value)
                  }
                  placeholder="Enter file name"
                  disabled={readOnly}
                />
              </Form.Group>
            </Col>
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}
